import React, { useState, useEffect } from 'react';

import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import FormContainer from '../../../FormContainer';
import AddOrEditPageTabs from '../../../AddOrEditPageTabs';

import LenderService from '../../../../../services/LenderService';
import LoanService from '../../../../../services/LoanService';
import { useUiContext } from '../../../../../context/UiContext';
import useLoanForm from '../../../../../forms/loan/loan';
import { useServicerStore } from '../../../../../store';
import useUnderwritingForm from '../../../../../forms/loan/underwriting';
import UnderwritingService from '../../../../../services/UnderwritingService';

const createLoanSettings = (lender) => {
  if (!lender) {
    return {};
  }

  return {
    pref_tax_impound_shortage: lender.pref_tax_impound_shortage,
    pref_insurance_cancel: lender.pref_insurance_cancel,
    pref_hoa_impound_shortage: lender.pref_hoa_impound_shortage,
    impound_cushion: lender.default_impound_cushion,
    servicer_penalty_split: lender.default_penalty_split,
    payment_waterfall: lender.default_payment_waterfall,
  };
};

export default function Loan({ isEdit, loan, isVisible, refetch, readOnly }) {
  const [currentLender, setCurrentLender] = useState();
  const [currentTab, setCurrentTab] = useState('Note');

  const navigate = useNavigate();

  const { showToast } = useUiContext();

  const userRole = useServicerStore((state) => state.userRole);
  const isLender = userRole === 'lender';

  const { mutate: createLoan, isPending: isLoanCreating } = useMutation({
    mutationFn: (formValues) =>
      LoanService.createLoan({
        ...formValues,
        ...createLoanSettings(currentLender),
      }),
    onSuccess: (data) => {
      navigate(`/loans/${data.id}?tab=Borrowers`, { replace: true });
      showToast({
        text: 'Loan created',
        type: 'success',
      });
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: updateLoan, isPending: isLoanUpdating } = useMutation({
    mutationFn: (values) => {
      const shouldChangeOptions = values.lender_id !== loan.lender_id;

      let options = {};
      if (shouldChangeOptions) {
        options = createLoanSettings(currentLender);
      }

      return LoanService.updateLoan({
        formValues: { ...values, ...options },
        loanId: loan.id,
      });
    },
    onSuccess: () => {
      showToast({
        text: 'Loan updated',
        type: 'success',
      });
      refetch();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: createUnderwriting, isPending: isUnderwritingCreating } =
    useMutation({
      mutationFn: (formValues) =>
        UnderwritingService.createUnderWriting({
          ...Object.keys(formValues).reduce((acc, key) => {
            acc[key] = formValues[key] === '' ? null : formValues[key];
            return acc;
          }, {}),
          loan_id: loan.id,
        }),
      onSuccess: () => {
        refetch();
        showToast({
          text: 'Underwriting created',
          type: 'success',
        });
      },
      onError: (error) => {
        console.log(error);
        showToast({
          text: error?.response?.data?.message || 'Something went wrong',
          type: 'error',
        });
      },
    });

  const { mutate: updateUnderwriting, isPending: isUnderwritingUpdating } =
    useMutation({
      mutationFn: (formValues) =>
        UnderwritingService.updateUnderWriting({
          formValues: Object.keys(formValues).reduce((acc, key) => {
            acc[key] = formValues[key] === '' ? null : formValues[key];
            return acc;
          }, {}),
          underwritingId: loan?._underwriting?.id,
        }),
      onSuccess: () => {
        refetch();
        showToast({
          text: 'Underwriting updated',
          type: 'success',
        });
      },
      onError: (error) => {
        console.log(error);
        showToast({
          text: error?.response?.data?.message || 'Something went wrong',
          type: 'error',
        });
      },
    });

  const loanForm = useLoanForm(
    isEdit ? updateLoan : createLoan,
    loan,
    isLender,
    currentTab
  );

  const lenderId = loanForm?.formik?.values?.lender_id;

  const { data: lender } = useQuery({
    queryKey: ['lender', lenderId],
    queryFn: () => LenderService.getLender(lenderId),
    enabled: !!lenderId,
  });

  useEffect(() => {
    if (lender) {
      setCurrentLender(lender);
    }
  }, [lender]);

  const tabs = [
    {
      label: 'Note',
      active: currentTab === 'Note',
      onClick: () => {
        setCurrentTab('Note');
      },
    },
    {
      label: 'Impounds',
      active: currentTab === 'Impounds',
      onClick: () => {
        setCurrentTab('Impounds');
      },
    },
    {
      label: 'DOT',
      active: currentTab === 'DOT',
      onClick: () => {
        setCurrentTab('DOT');
      },
    },
    {
      label: 'Underwriting',
      active: currentTab === 'Underwriting',
      onClick: () => {
        setCurrentTab('Underwriting');
      },
      isHidden: !isEdit,
    },
  ];

  const hasUnderwriting = !!loan?._underwriting;

  const underwritingForm = useUnderwritingForm(
    hasUnderwriting ? updateUnderwriting : createUnderwriting,
    loan?._underwriting
  );

  const isUnderwritingTab = currentTab === 'Underwriting';
  const isPending =
    isLoanCreating ||
    isLoanUpdating ||
    isUnderwritingCreating ||
    isUnderwritingUpdating;

  return (
    <div>
      <div className="mb-4">
        <AddOrEditPageTabs tabs={tabs} />
      </div>
      <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
        <FormContainer
          headerRight={
            !isUnderwritingTab && (
              <span className="text-red-400 text-sm">
                {loan?.pixel_loan_number}
              </span>
            )
          }
          form={isUnderwritingTab ? underwritingForm : loanForm}
          isVisible={isVisible}
          readOnly={readOnly}
          noBillComId={isLender || isUnderwritingTab}
          isSubmitting={isPending}
        />
      </div>
    </div>
  );
}
